/**
 * RequireAuth — route-level auth guard.
 *
 * Wraps protected route groups in `router.tsx` so that unauthenticated users
 * are redirected to `/auth` instead of seeing a broken dashboard shell. While
 * the auth session is still loading (the `StubAuthProvider` mounts the mock
 * session after the first paint), we show the same full-page loader the lazy
 * route Suspense boundary already uses — zero visual jump.
 *
 * Usage in router.tsx:
 *   { element: <RequireAuth />, children: [all protected routes...] }
 *
 * Ref: issue #80.
 */
import type { ReactElement } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { MatrixLoader } from '@shared/ui';
import { usePostHogIdentify } from '@shared/analytics';
import { useAuth } from './use-auth';

export function RequireAuth(): ReactElement {
  const { status } = useAuth();
  const location = useLocation();
  usePostHogIdentify();

  if (status === 'loading') {
    return (
      <div className="text-aq-accent flex min-h-screen items-center justify-center">
        <MatrixLoader pattern="wave" size="md" />
      </div>
    );
  }

  if (status !== 'authenticated') {
    // Preserve the intended destination so a future "redirect after login"
    // feature can pick it up from `location.state.from`.
    return <Navigate to="/auth" state={{ from: location }} replace />;
  }

  return <Outlet />;
}
